import { useMemo } from 'react';
import type { OrgPathNode } from '../types';

export interface CatchballDiagramTrack {
  name: string;
  color: string;
  path: Array<{
    id: string;
    name: string;
    title: string;
    relativeLevel: number;
    tier: 'executive' | 'middle' | 'bottom';
  }>;
}

interface CatchballDiagramProps {
  track: CatchballDiagramTrack;
  mode?: 'preview' | 'live';
  className?: string;
}

const TIER_ORDER: Array<'executive' | 'middle' | 'bottom'> = ['executive', 'middle', 'bottom'];

const TIER_LABELS: Record<'executive' | 'middle' | 'bottom', { label: string; color: string }> = {
  executive: { label: 'Exec', color: '#D4A017' },
  middle: { label: 'Mid', color: '#6B5B8C' },
  bottom: { label: 'Team', color: '#3A7D56' },
};

// Pre-existing: this helper ships alongside the component it feeds. Flagged only
// because editing this file brings it into CI's changed-file lint at --max-warnings=0.
// eslint-disable-next-line react-refresh/only-export-components
export function pathToDiagramTrack(
  pathName: string,
  color: string,
  nodeIds: string[],
  orgById: Map<string, OrgPathNode>,
  maxRelativeLevel: number,
): CatchballDiagramTrack {
  const path = nodeIds
    .map((id) => orgById.get(id))
    .filter(Boolean)
    .map((n) => {
      const node = n!;
      let tier: 'executive' | 'middle' | 'bottom' = 'middle';
      if (node.relativeLevel <= 0) tier = 'executive';
      else if (node.relativeLevel >= maxRelativeLevel && maxRelativeLevel > 0) tier = 'bottom';
      return {
        id: node.id,
        name: node.name,
        title: node.title,
        relativeLevel: node.relativeLevel,
        tier,
      };
    });

  return { name: pathName, color, path };
}

export function CatchballDiagram({ track, mode = 'preview', className }: CatchballDiagramProps) {
  const tiers = useMemo(() => {
    const active = TIER_ORDER.filter((tier) => track.path.some((n) => n.tier === tier));
    return active.map((tierId) => ({
      tierId,
      nodes: track.path.filter((n) => n.tier === tierId),
    }));
  }, [track.path]);

  const TIER_H = 60;
  const GAP_H = 24;
  const SVG_W = 48;
  const HALF = SVG_W / 2;
  const svgH = tiers.length * TIER_H + Math.max(0, tiers.length - 1) * GAP_H;
  const tierY = tiers.map((_, i) => i * (TIER_H + GAP_H) + TIER_H / 2);

  let downPaths = '';
  let upPaths = '';
  for (let i = 0; i < tiers.length - 1; i++) {
    const y1 = tierY[i] + TIER_H / 2;
    const y2 = tierY[i + 1] - TIER_H / 2;
    const xDown = HALF * 0.45;
    const xUp = HALF * 1.55;
    const mid = (y1 + y2) / 2;
    downPaths += `<path d="M ${xDown} ${y1} C ${xDown} ${mid}, ${xDown} ${mid}, ${xDown} ${y2}" stroke="var(--rule-bright)" stroke-width="1" fill="none" opacity="0.4"/>`;
    upPaths += `<path d="M ${xUp} ${y2} C ${xUp} ${mid}, ${xUp} ${mid}, ${xUp} ${y1}" stroke="var(--rule-bright)" stroke-width="1" fill="none" opacity="0.4"/>`;
  }

  return (
    <div
      className={className}
      style={{
        display: 'flex',
        border: '1px solid var(--rule)',
        background: 'var(--bg)',
        overflow: 'hidden',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            padding: '7px 10px',
            borderBottom: '1px solid var(--rule)',
          }}
        >
          <div
            style={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              background: track.color,
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontSize: '0.95rem',
              fontWeight: 500,
              color: 'var(--text-sec)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {track.name}
          </span>
          {mode === 'preview' && (
            <span
              style={{
                fontFamily: 'var(--mono)',
                fontSize: '0.6rem',
                letterSpacing: '0.1em',
                color: 'var(--text-dim)',
                marginLeft: 'auto',
                textTransform: 'uppercase',
              }}
            >
              Preview
            </span>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {tiers.map(({ tierId, nodes }, i) => {
            const def = TIER_LABELS[tierId];
            // Label the rung with what the org actually holds, not the catchball
            // tier word. The deepest level in scope is tier 'bottom' whatever it
            // is, so a two-level org had its DEPARTMENTS labelled "Team" — which
            // read as an instruction to add a team level that was never required.
            // `title` is the org unit's own type ("department", "organization").
            const orgTypes = Array.from(new Set(nodes.map((n) => n.title).filter(Boolean)));
            const rungLabel = orgTypes.length === 1 ? orgTypes[0]! : def.label;
            return (
              <div key={tierId}>
                <div
                  style={{
                    height: TIER_H,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    padding: '0 10px',
                  }}
                >
                  <div
                    style={{
                      fontFamily: 'var(--mono)',
                      fontSize: '0.58rem',
                      letterSpacing: '0.14em',
                      textTransform: 'uppercase',
                      color: def.color,
                      marginBottom: 3,
                    }}
                  >
                    {rungLabel}
                  </div>
                  <div
                    style={{
                      fontSize: '0.92rem',
                      color: 'var(--text-sec)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {nodes.map((n) => n.name).join(' → ')}
                  </div>
                  <div
                    style={{
                      fontSize: '0.72rem',
                      color: 'var(--text-dim)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      marginTop: 1,
                    }}
                  >
                    {nodes.map((n) => n.title).join(', ')}
                  </div>
                </div>
                {i < tiers.length - 1 && <div style={{ height: GAP_H }} />}
              </div>
            );
          })}
        </div>
      </div>
      <div
        style={{ width: SVG_W, flexShrink: 0, borderLeft: '1px solid var(--rule)' }}
        dangerouslySetInnerHTML={{
          __html: `<svg width="${SVG_W}" height="${svgH}" viewBox="0 0 ${SVG_W} ${svgH}">${downPaths}${upPaths}</svg>`,
        }}
      />
    </div>
  );
}
